// ビジュアルスタイルの定義
export interface VisualStyle {
  id: string
  name: string
  description: string
  className: string  // Tailwind CSSクラス
  supportedTypes: string[]  // このスタイルが対応している文献タイプ
}

// 日記のビジュアルスタイル
export const diaryStyles: VisualStyle[] = [
  {
    id: 'warm',
    name: '温かい日記',
    description: '手書き風の温かみのある日記',
    className: 'bg-amber-50 border-amber-200 text-amber-900 shadow-sm',
    supportedTypes: ['diary']
  },
  {
    id: 'cold',
    name: '無機質な手記',
    description: '冷たく機械的な記録',
    className: 'bg-gray-100 border-gray-400 text-gray-800 font-mono',
    supportedTypes: ['diary']
  },
  {
    id: 'horror',
    name: '不穏な日記',
    description: '薄汚れたホラー風の日記',
    className: 'bg-red-950/10 border-red-900/30 text-red-950 shadow-lg diary-horror',
    supportedTypes: ['diary']
  },
  {
    id: 'aged',
    name: '古びた日記',
    description: '年月を経た古い日記',
    className: 'bg-yellow-100/80 border-yellow-800/40 text-yellow-950 diary-aged',
    supportedTypes: ['diary']
  }
]

// メモのビジュアルスタイル
export const memoStyles: VisualStyle[] = [
  {
    id: 'sticky',
    name: '付箋メモ',
    description: 'カラフルな付箋風メモ',
    className: 'bg-yellow-200 border-yellow-400 text-gray-800 shadow-md rotate-1',
    supportedTypes: ['memo']
  },
  {
    id: 'notebook',
    name: 'ノート',
    description: '罫線入りノート',
    className: 'bg-white border-gray-300 text-gray-800 memo-notebook',
    supportedTypes: ['memo']
  },
  {
    id: 'torn',
    name: '破れたメモ',
    description: '端が破れた古いメモ',
    className: 'bg-gray-50 border-gray-500 text-gray-900 memo-torn',
    supportedTypes: ['memo']
  },
  {
    id: 'bloodstained',
    name: '血痕のメモ',
    description: '血で汚れた不気味なメモ',
    className: 'bg-red-50/50 border-red-800/50 text-red-950 memo-blood',
    supportedTypes: ['memo']
  }
]

// 新聞のビジュアルスタイル
export const newspaperStyles: VisualStyle[] = [
  {
    id: 'modern',
    name: '現代新聞',
    description: '現代的なデジタル新聞',
    className: 'bg-white border-gray-200 text-gray-900 newspaper-modern',
    supportedTypes: ['newspaper']
  },
  {
    id: 'classic',
    name: 'クラシック新聞',
    description: '20世紀中期の新聞',
    className: 'bg-gray-50 border-gray-400 text-gray-900 newspaper-classic',
    supportedTypes: ['newspaper']
  },
  {
    id: 'vintage',
    name: 'ビンテージ新聞',
    description: '戦前の古い新聞',
    className: 'bg-yellow-50/70 border-yellow-900/30 text-gray-900 newspaper-vintage',
    supportedTypes: ['newspaper']
  },
  {
    id: 'war',
    name: '戦時新聞',
    description: '戦時中の検閲済み新聞',
    className: 'bg-gray-100 border-gray-600 text-gray-900 newspaper-war',
    supportedTypes: ['newspaper']
  },
  {
    id: 'tabloid',
    name: 'タブロイド紙',
    description: 'ゴシップ系タブロイド紙',
    className: 'bg-red-50 border-red-300 text-gray-900 newspaper-tabloid',
    supportedTypes: ['newspaper']
  }
]

// 雑誌のビジュアルスタイル
export const magazineStyles: VisualStyle[] = [
  {
    id: 'modern',
    name: 'モダン雑誌',
    description: '現代的なデザインの雑誌',
    className: 'bg-white text-gray-900',
    supportedTypes: ['magazine']
  },
  {
    id: 'fashion',
    name: 'ファッション誌',
    description: 'スタイリッシュなファッション雑誌',
    className: 'bg-gradient-to-br from-pink-50 to-purple-50',
    supportedTypes: ['magazine']
  },
  {
    id: 'business',
    name: 'ビジネス誌',
    description: '真面目なビジネス雑誌',
    className: 'bg-white border-gray-300',
    supportedTypes: ['magazine']
  },
  {
    id: 'interview',
    name: 'インタビュー誌',
    description: 'インタビュー特化の雑誌',
    className: 'bg-gradient-to-b from-gray-50 to-white',
    supportedTypes: ['magazine']
  }
]

// 歴史書のビジュアルスタイル
export const historyStyles: VisualStyle[] = [
  {
    id: 'modern',
    name: '現代書籍',
    description: '現代的な歴史書',
    className: 'bg-white text-gray-900',
    supportedTypes: ['history']
  },
  {
    id: 'scroll',
    name: '巻物',
    description: '古代の巻物風',
    className: 'bg-gradient-to-b from-yellow-100 to-yellow-50',
    supportedTypes: ['history']
  },
  {
    id: 'ancient',
    name: '古文書',
    description: '古代の文書風',
    className: 'bg-gradient-to-br from-amber-100 via-amber-50 to-yellow-50',
    supportedTypes: ['history']
  },
  {
    id: 'medieval',
    name: '中世写本',
    description: '中世ヨーロッパの写本風',
    className: 'bg-gradient-to-b from-amber-50 to-white',
    supportedTypes: ['history']
  }
]

// すべてのスタイルを統合
export const allVisualStyles: VisualStyle[] = [
  ...diaryStyles,
  ...memoStyles,
  ...newspaperStyles,
  ...magazineStyles,
  ...historyStyles
]

// 文献タイプに応じたスタイルを取得
export function getStylesForType(documentType: string): VisualStyle[] {
  switch (documentType) {
    case 'diary':
      return diaryStyles
    case 'memo':
      return memoStyles
    case 'newspaper':
      return newspaperStyles
    case 'magazine':
      return magazineStyles
    case 'history':
      return historyStyles
    default:
      return []
  }
}

// デフォルトスタイルを取得
export function getDefaultStyle(documentType: string): string {
  switch (documentType) {
    case 'diary':
      return 'warm'
    case 'memo':
      return 'sticky'
    case 'newspaper':
      return 'modern'
    case 'magazine':
      return 'modern'
    case 'history':
      return 'modern'
    default:
      return ''
  }
}